import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import BestDoctorsIndia from '../components/StarDoctorsIndia';
import TestimonialVideoCarousel from '../components/TestimonialVideoCarousel';
import BlogsCarousel from '../components/BlogsCarousel';
import TopHospitalsIndia from '../components/TopHospitalsIndia';
import { Link } from 'react-router-dom';
import { getDoctorSpecialitiesForCountry } from '../utils/doctorUtils';
import api from '../services/api';

const BestDoctorsInIndia = () => {
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('');

  useEffect(() => {
    const fetchSpecialities = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the same API service that other components use successfully
        try {
          // First try to get specialities from the dedicated endpoint
          try {
            const specialitiesResponse = await api.get('/doctors/specialities/India');
            if (specialitiesResponse.data.specialities && specialitiesResponse.data.specialities.length > 0) {
              setSpecialties(specialitiesResponse.data.specialities);
              setDataSource(`API (Specialities Endpoint - ${specialitiesResponse.data.count} specialities)`);
              setError(null);
              return;
            }
          } catch (specialitiesError) {
            console.warn('Specialities endpoint failed, trying general doctors endpoint:', specialitiesError.message);
          }

          // Fallback to general doctors endpoint
          const response = await api.get('/doctors', { 
            params: { limit: 100 } 
          });
          
          let doctorsData = [];
          
          // Handle API response format
          if (response.data.doctors && Array.isArray(response.data.doctors)) {
            doctorsData = response.data.doctors;
          } else if (Array.isArray(response.data)) {
            doctorsData = response.data;
          }
          
          // Filter by country (India) on frontend
          const indiaDoctors = doctorsData.filter(doctor => 
            doctor.country && doctor.country.toLowerCase() === 'india'
          );
          
          // Extract specialities from India doctors data
          const specialitiesSet = new Set();
          indiaDoctors.forEach(doctor => {
            if (doctor.specialty && doctor.specialty.trim()) {
              specialitiesSet.add(doctor.specialty.trim());
            }
          });
          
          const extractedSpecialities = Array.from(specialitiesSet).sort();
          
          if (extractedSpecialities.length > 0) {
            // Format specialities for display - just add "Best" and "Doctors in India"
            const formattedSpecialities = extractedSpecialities.map(spec => 
              `Best ${spec} Doctors in India`
            );
            setSpecialties(formattedSpecialities);
            setDataSource(`API (Live Database - ${indiaDoctors.length} India doctors)`);
            setError(null);
          } else {
            throw new Error('No specialities found in India doctor data');
          }
          
        } catch (apiError) {
          console.warn('API call failed, using local data:', apiError.message);
          // Use local data as fallback
          const localSpecialities = getDoctorSpecialitiesForCountry('India');
          if (localSpecialities.length > 0) {
            setSpecialties(localSpecialities);
            setDataSource('Local Data');
            setError('API temporarily unavailable, showing data from local cache');
          } else {
            setSpecialties([]);
            setDataSource('No Data');
            setError('Unable to load doctor specialities');
          }
        }
        
      } catch (err) {
        console.error('Error in fetchSpecialities:', err);
        setError('Unable to load doctor data');
        setDataSource('Error');
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialities();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-12 text-center">Best Doctors in India</h1>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1976f2]"></div>
            <p className="mt-4 text-gray-600">Loading specialities...</p>
          </div>
        </main>
        <Footer />
      <FloatingChatButton />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-12 text-center">Best Doctors in India</h1>
        
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-center max-w-4xl mx-auto">
            <p className="text-yellow-800">
              <strong>Note:</strong> {error}
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {specialties.map((label, idx) => (
            <Link
              key={idx}
              to={`/bestdoctordetails?speciality=${encodeURIComponent(label)}&country=India`}
              className="flex items-center justify-center border-2 border-[#1976f2] rounded-2xl p-4 text-base font-semibold text-blue-900 bg-white text-center transition hover:border-blue-800 hover:shadow-md"
            >
              {label}
            </Link>
          ))}
        </div>
        
        {specialties.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No specialities available at the moment.</p>
            <p className="text-gray-500 text-sm mt-2">Please check back later or contact support.</p>
          </div>
        )}
        
        {/* Intro Paragraph for Top Doctors */}
        <div className="max-w-4xl mx-auto mt-12 mb-8 text-center text-gray-600 text-base md:text-lg font-medium">
          Looking for the best doctors in India? Whether you need expert cardiology care, oncology treatment, neurosurgery consultation, or orthopedic expertise, we help you
          find top-rated doctors across every medical specialty. Our curated list includes the best doctors for heart care, cancer treatment, gynecology, pediatrics, urology,
          gastroenterology, and more. Compare doctors, read about their qualifications, and book consultations with ease. Discover trusted medical
          professionals with world-class expertise and proven track records.
        </div>

        {/* Top Doctors Carousel Section */}
        <section className="mt-16">
          <BestDoctorsIndia />
        </section>
        {/* Top Hospitals Carousel Section */}
        <section className="mt-16">
          <TopHospitalsIndia />
        </section>

        {/* Testimonial Videos Section */}
        <section className="container mx-auto px-4 pt-4 pb-8 mt-16">
          <TestimonialVideoCarousel />
        </section>

        {/* Recent Blogs Section */}
        <section className="container mx-auto px-4 py-12">
          <BlogsCarousel />
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BestDoctorsInIndia; 